import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { api, setToken } from "@/lib/api";
import { inputCls, labelCls } from "./AdminUI";

export default function AdminLogin() {
  const nav = useNavigate();
  // login | reset | reset-sent | reset-confirm
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [token, setTok] = useState("");
  const [newPw, setNewPw] = useState("");
  const [resetMsg, setResetMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [checkingLink, setCheckingLink] = useState(false);

  // Only a VALID, non-expired reset link may open the reset-confirm screen.
  // Any other state (expired/invalid/missing token, normal login, Safari
  // storage cleanup, PWA/tab restore of a stale ?reset= URL) stays on / returns
  // to the normal email+password login.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("reset");
    if (!p) return;
    setCheckingLink(true);
    api.get(`/auth/reset-validate?token=${encodeURIComponent(p)}`)
      .then((r) => {
        if (r.data?.valid) {
          setTok(p);
          setMode("reset-confirm");
        } else {
          window.history.replaceState({}, "", "/admin/login");
          setMode("login");
          toast.error("That password reset link is invalid or expired. Please log in or request a new link.");
        }
      })
      .catch(() => {
        window.history.replaceState({}, "", "/admin/login");
        setMode("login");
        toast.error("That password reset link is invalid or expired. Please log in or request a new link.");
      })
      .finally(() => setCheckingLink(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password });
      setToken(r.data.token);
      nav("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/reset-request", { email });
      setResetMsg(r.data.message || "If an account exists for that email, a password reset link has been sent.");
      setMode("reset-sent");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/reset-confirm", { token, new_password: newPw });
      toast.success("Password updated. Please log in with your new password.");
      setMode("login"); setTok(""); setNewPw("");
      window.history.replaceState({}, "", "/admin/login");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
      // A rejected token must never trap the user here — return to normal login.
      setMode("login"); setTok("");
      window.history.replaceState({}, "", "/admin/login");
    }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--navy)] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/brand/logo.png" alt="Veterans Power Wash" className="h-20 w-20 rounded-2xl mx-auto" />
          <h1 className="font-head font-extrabold text-white text-2xl uppercase mt-4 tracking-tight">Admin Portal</h1>
          <p className="text-white/50 text-sm">Veterans Power Wash & Cleaning Services</p>
        </div>

        <div className="bg-white rounded-2xl p-7 card-shadow-lg">
          {checkingLink && (
            <div className="flex items-center justify-center gap-2 text-gray-500 py-6" data-testid="reset-link-checking">
              <Loader2 className="w-4 h-4 animate-spin" /> Verifying reset link…
            </div>
          )}

          {!checkingLink && mode === "login" && (
            <form onSubmit={login} className="space-y-4" data-testid="admin-login-form">
              <div><label className={labelCls}>Email</label>
                <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" required /></div>
              <div><label className={labelCls}>Password</label>
                <input className={inputCls} type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" required /></div>
              <button type="submit" disabled={busy} data-testid="login-submit"
                className="w-full flex items-center justify-center gap-2 bg-[var(--red)] hover:bg-[var(--red-dark)] text-white font-semibold py-2.5 rounded-md">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Sign In
              </button>
              <button type="button" onClick={() => setMode("reset")} className="w-full text-sm text-[var(--action)] hover:underline" data-testid="forgot-password-link">Forgot password?</button>
            </form>
          )}

          {!checkingLink && mode === "reset" && (
            <form onSubmit={requestReset} className="space-y-4" data-testid="reset-request-form">
              <p className="text-sm text-gray-500">Enter your email and we'll send you a secure password reset link.</p>
              <div><label className={labelCls}>Email</label>
                <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reset-email" required /></div>
              <button type="submit" disabled={busy} data-testid="reset-request-submit" className="w-full flex items-center justify-center gap-2 bg-[var(--action)] text-white font-semibold py-2.5 rounded-md">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Send Reset Link</button>
              <button type="button" onClick={() => setMode("login")} className="w-full text-sm text-gray-500 flex items-center justify-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to login</button>
            </form>
          )}

          {!checkingLink && mode === "reset-sent" && (
            <div className="space-y-4 text-center" data-testid="reset-sent-message">
              <MailCheck className="w-10 h-10 text-[var(--action)] mx-auto" />
              <p className="text-sm text-gray-600">{resetMsg}</p>
              <p className="text-xs text-gray-400">Open the reset link in that email to choose a new password. The link expires in 1 hour.</p>
              <button type="button" onClick={() => setMode("login")} className="w-full text-sm text-gray-600 flex items-center justify-center gap-1" data-testid="reset-sent-back"><ArrowLeft className="w-3 h-3" /> Back to login</button>
            </div>
          )}

          {!checkingLink && mode === "reset-confirm" && (
            <form onSubmit={confirmReset} className="space-y-4" data-testid="reset-confirm-form">
              <p className="text-sm text-gray-500">Your reset link is valid. Choose a new password to finish.</p>
              <div><label className={labelCls}>New Password (min 8 chars)</label>
                <input className={inputCls} type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="reset-newpw" minLength={8} required /></div>
              <button type="submit" disabled={busy} data-testid="reset-confirm-submit" className="w-full flex items-center justify-center gap-2 bg-[var(--red)] text-white font-semibold py-2.5 rounded-md">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Set New Password</button>
              <button type="button" onClick={() => { setMode("login"); setTok(""); window.history.replaceState({}, "", "/admin/login"); }} className="w-full text-sm text-gray-500 flex items-center justify-center gap-1"><ArrowLeft className="w-3 h-3" /> Back to login</button>
            </form>
          )}
        </div>
        <p className="text-center text-white/30 text-xs mt-6">Authorized access only.</p>
      </div>
    </div>
  );
}
